import { Trans, useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import { useAppDialog } from "@shared/context/AppDialogContext";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import apiClient from "@shared/services/apiClient";
import {
  ReviewTemplate,
  TemplateDraft,
  TEMPLATE_CATEGORIES,
} from "./customizationTypes";
import {
  getErrorMessage,
  parseJsonObject,
  toDateTimeLabel,
  unwrapApiData,
} from "./customizationUtils";

const TemplatesManagerPanel: React.FC = () => {
  const { t } = useTranslation("features");
  const dialog = useAppDialog();
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, TemplateDraft>>({});
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("icu");
  const [newConfigText, setNewConfigText] = useState("{}");
  const [newLocked, setNewLocked] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        categoryFilter === "all"
          ? "/eeg/review-templates"
          : `/eeg/review-templates?category=${encodeURIComponent(categoryFilter)}`;
      const response = await apiClient.get(endpoint);
      const data = unwrapApiData<ReviewTemplate[]>(response);
      const rows = Array.isArray(data) ? data : [];
      setTemplates(rows);
      const draftMap: Record<string, TemplateDraft> = {};
      rows.forEach((row) => {
        draftMap[row.template_id] = {
          name: row.name,
          configText: JSON.stringify(row.config_json ?? {}, null, 2),
          isLocked: row.is_locked,
        };
      });
      setDrafts(draftMap);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const createTemplate = async () => {
    if (!newName.trim()) {
      toast.error(t("eegCustomization.templates.nameRequired"));
      return;
    }
    try {
      const config = parseJsonObject(
        newConfigText,
        t("eegCustomization.templates.configuration"),
      );
      await apiClient.post("/eeg/review-templates", {
        name: newName.trim(),
        category: newCategory,
        config_json: config,
        is_locked: newLocked,
      });
      toast.success(t("eegCustomization.templates.created"));
      setNewName("");
      setNewConfigText("{}");
      setNewLocked(false);
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const saveTemplate = async (templateId: string) => {
    const draft = drafts[templateId];
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error(t("eegCustomization.templates.nameRequired"));
      return;
    }
    try {
      const config = parseJsonObject(
        draft.configText,
        t("eegCustomization.templates.configuration"),
      );
      await apiClient.put(`/eeg/review-templates/${templateId}`, {
        name: draft.name.trim(),
        config_json: config,
        is_locked: draft.isLocked,
      });
      toast.success(t("eegCustomization.templates.updated"));
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const cloneTemplate = async (template: ReviewTemplate) => {
    const cloneName = await dialog.prompt({
      message: t("eegCustomization.templates.clonePrompt"),
      defaultValue: `${template.name} (${t("eegCustomization.templates.copySuffix")})`,
    });
    if (!cloneName?.trim()) return;
    try {
      await apiClient.post(
        `/eeg/review-templates/${template.template_id}/clone`,
        {
          name: cloneName.trim(),
        },
      );
      toast.success(t("eegCustomization.templates.cloned"));
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const deleteTemplate = async (template: ReviewTemplate) => {
    if (
      !(await dialog.confirm(
        t("eegCustomization.templates.removeConfirm", { name: template.name }),
      ))
    ) {
      return;
    }
    try {
      await apiClient.delete(`/eeg/review-templates/${template.template_id}`);
      toast.success(t("eegCustomization.templates.removed"));
      await loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{t("eegCustomization.templates.title")}</h5>
          <div className="d-flex gap-2">
            <Form.Select
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              className="customization-category-filter"
              aria-label={t("eegCustomization.templates.allCategories")}
            >
              <option value="all">
                {t("eegCustomization.templates.allCategories")}
              </option>
              {TEMPLATE_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category.toUpperCase()}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="outline-secondary"
              onClick={loadTemplates}
              className="customization-refresh-button"
            >
              <Trans ns="features" i18nKey="ui.copy.56e3badc4e6c" />
            </Button>
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col md={3}>
            <Form.Control
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder={t("eegCustomization.templates.newName")}
              aria-label={t("eegCustomization.templates.newName")}
            />
          </Col>
          <Col md={2}>
            <Form.Select
              value={newCategory}
              onChange={(event) => setNewCategory(event.target.value)}
              aria-label={t("eegCustomization.templates.category")}
            >
              {TEMPLATE_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category.toUpperCase()}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={5}>
            <Form.Control
              as="textarea"
              rows={3}
              className="json-editor"
              value={newConfigText}
              onChange={(event) => setNewConfigText(event.target.value)}
              aria-label={t("eegCustomization.templates.configJson")}
            />
          </Col>
          <Col md={2}>
            <Form.Check
              id="template-create-locked"
              className="mb-2"
              type="switch"
              label={t("eegCustomization.templates.locked")}
              checked={newLocked}
              onChange={(event) => setNewLocked(event.target.checked)}
            />
            <Button
              variant="primary"
              onClick={createTemplate}
              className="w-100"
            >
              {t("eegCustomization.templates.create")}
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : templates.length === 0 ? (
          <Alert variant="light" className="mb-0">
            {t("eegCustomization.templates.empty")}
          </Alert>
        ) : (
          <div className="table-responsive">
            <Table bordered hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>
                    <Trans ns="features" i18nKey="ui.copy.709a23220f2c" />
                  </th>
                  <th>
                    <Trans ns="features" i18nKey="ui.copy.a3c686e711e4" />
                  </th>
                  <th>
                    <Trans ns="features" i18nKey="ui.copy.eb334eca0cff" />
                  </th>
                  <th>
                    <Trans ns="features" i18nKey="ui.copy.057d58c74d80" />
                  </th>
                  <th>
                    <Trans ns="features" i18nKey="ui.copy.f2f8570ddd7b" />
                  </th>
                  <th>
                    <Trans ns="features" i18nKey="ui.copy.c3cd636a585b" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => {
                  const draft = drafts[template.template_id];
                  return (
                    <tr key={template.template_id}>
                      <td>
                        <Form.Control
                          value={draft?.name || ""}
                          aria-label={`${t("eegCustomization.templates.name")} ${template.name}`}
                          onChange={(event) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [template.template_id]: {
                                ...(prev[template.template_id] || {
                                  name: template.name,
                                  configText: "{}",
                                  isLocked: template.is_locked,
                                }),
                                name: event.target.value,
                              },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <Badge bg="secondary">
                          {template.category.toUpperCase()}
                        </Badge>
                        {template.is_locked && (
                          <Badge bg="dark" className="ms-2">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.a798882f1c31"
                            />
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="json-editor"
                          value={draft?.configText || "{}"}
                          aria-label={`${t("eegCustomization.templates.configJson")} ${template.name}`}
                          onChange={(event) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [template.template_id]: {
                                ...(prev[template.template_id] || {
                                  name: template.name,
                                  configText: "{}",
                                  isLocked: template.is_locked,
                                }),
                                configText: event.target.value,
                              },
                            }))
                          }
                        />
                        <Form.Check
                          id={`template-lock-${template.template_id}`}
                          type="switch"
                          label={t("eegCustomization.templates.locked")}
                          checked={draft?.isLocked || false}
                          onChange={(event) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [template.template_id]: {
                                ...(prev[template.template_id] || {
                                  name: template.name,
                                  configText: "{}",
                                  isLocked: template.is_locked,
                                }),
                                isLocked: event.target.checked,
                              },
                            }))
                          }
                        />
                      </td>
                      <td>
                        {(template.assignments || []).length === 0 ? (
                          <span className="text-muted">
                            <Trans
                              ns="features"
                              i18nKey="ui.copy.373fdeb4e662"
                            />
                          </span>
                        ) : (
                          template.assignments?.map((assignment) => (
                            <div
                              key={`${template.template_id}-${assignment.role_name}`}
                            >
                              {assignment.role_name}
                              {assignment.is_default ? " (default)" : ""}
                            </div>
                          ))
                        )}
                      </td>
                      <td>{toDateTimeLabel(template.updated_at)}</td>
                      <td className="customization-actions-cell">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => saveTemplate(template.template_id)}
                        >
                          <Trans ns="features" i18nKey="ui.copy.efc007a393f6" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => cloneTemplate(template)}
                        >
                          <Trans ns="features" i18nKey="ui.copy.d8cdb573350d" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => deleteTemplate(template)}
                        >
                          <Trans ns="features" i18nKey="ui.copy.f6fdbe48dc54" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TemplatesManagerPanel;
